<template>
  <section v-if="tourhero">
    <SectionHeading icon="icon-single-neutral-actions-star" title="Your TourHero" />

    <img v-if="tourhero.avatar" :src="tourhero.avatar" />

    <div class="profile">
      <div v-if="name">
        <label class="label">tourhero</label>
        <div>{{ name }}</div>
      </div>

      <div v-if="bio">
        <label class="label">bio</label>
        <div class="bio" v-html="parseMarkdown(bio)" />
      </div>

      <div v-if="description">
        <label class="label">bio</label>
        <div class="description" v-html="parseMarkdown(description)"></div>
      </div>

      <div v-if="tourhero.instagramUsername || tourhero.tiktokUsername">
        <div class="label">social</div>
        <div class="social-links">
          <a v-if="instagramUsername" :href="`https://instagram.com/${instagramUsername}`" target="_blank">
            <i class="icon icon-instagram"></i>
            {{ instagramUsername }}
          </a>

          <a v-if="tiktokUsername" :href="`https://tiktok.com/@${tiktokUsername}`" target="_blank">
            <i class="icon icon-tiktok"></i>
            {{ tiktokUsername }}
          </a>
        </div>
      </div>

      <Button v-if="editable" color="green" invert @click="editProfile">Edit profile</Button>
    </div>
    <HeroBioUpdateDialog
      v-if="isEditProfileOpen && data?.itinerary.id"
      :productId="data?.itinerary.id"
      :avatar="tourhero.avatar"
      @close="isEditProfileOpen = false"
      :user="{
        name: tourhero.name,
        bio: isUser(tourhero) ? tourhero.bio : tourhero.description,
        instagramUsername: tourhero.instagramUsername,
        tiktokUsername: tourhero.tiktokUsername,
      }"
    />
  </section>
</template>

<script setup lang="ts">
import SectionHeading from '@/modules/Itinerary/Components/SectionHeading.vue'
import { computed, ref } from 'vue'
import { parseMarkdown } from '@/utils'
import Button from '@/components/Theme2/Button.vue'
import HeroBioUpdateDialog from './Components/HeroBioUpdateDialog.vue'
import { useTypedItineraryStore } from '../../stores/typed'
import { ItineraryHero, User, Product } from '@/models'
import { isItineraryHero, isUser } from '@/helpers/guards'

defineProps<{ editable: boolean }>()

const { data } = useTypedItineraryStore<Product>()
const tourhero = computed<ItineraryHero | User | null>(() => {
  if (!data?.itinerary.hero) return null
  if (!data?.tourheroes) return null

  if (isItineraryHero(data?.itinerary.hero) && data?.itinerary.hero.overrideHeroBioDisplay) {
    return data?.itinerary.hero
  }

  if (isUser(data?.tourheroes?.[0])) {
    return data?.tourheroes?.[0]
  }

  if (isItineraryHero(data?.tourheroes?.[0])) {
    return data?.tourheroes?.[0]
  }

  return null
})

const description = computed(() => (isItineraryHero(tourhero.value) ? tourhero.value.description : ''))
const bio = computed(() => (isUser(tourhero.value) ? tourhero.value.bio : ''))
const name = computed(() => tourhero.value?.name)
const instagramUsername = computed(() => tourhero.value?.instagramUsername)
const tiktokUsername = computed(() => tourhero.value?.tiktokUsername)

const isEditProfileOpen = ref(false)

const editProfile = () => {
  isEditProfileOpen.value = true
}
</script>

<style lang="scss" scoped>
section {
  display: grid;
  grid-template-columns: min(230px, 35%) 1fr;
  row-gap: 20px;
  column-gap: 24px;

  @include mobile {
    grid-template-columns: 1fr;
  }
}

img {
  border-radius: 15px;
  width: 100%;

  @include mobile {
    aspect-ratio: 1/1;
    justify-self: center;
  }
}

.profile {
  @include flex-column(24px);
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;

  @include desktop {
    align-items: flex-start;
  }
  @include mobile {
    gap: 16px;
  }
}

.label {
  color: $grey-100;
  font-size: 12px;
  line-height: 14px; /* 116.667% */
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.social-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;

  a,
  .icon {
    font-size: 16px;
    font-weight: 600;
    line-height: 150%; /* 24px */
  }
}

.bio:deep,
.description:deep {
  @include markdown-display;
  width: 100%;
}

.flex-row {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  color: $green-300;

  .max-width {
    width: 100%;
    word-break: break-all;
  }
}
</style>
